// ─── Canvas 2D renderer ─────────────────────────────────────────────────────
// Fresh browser renderer — the native app uses Skia; this uses Canvas 2D.
// Visual concept only (lanes, tokens, cascade particles, beat-grid pulse,
// near-miss flash), not a pixel port. All beat math comes from src/game/audioSync.ts.

import { LANE_COUNT, ROW_COUNT, type Chain, type Color } from '@gametypes';
import { COLOR_PALETTE, COLOR_GLOW } from '../palette';
import { inferBeatPhase } from '@game/audioSync';
import { beatPulseOpacity, chainMaxOpacity } from '@game/beatVisuals';
import type { Grid } from './state';

const BG = '#0D0D1A';
const LANE_BORDER = '#1E1E3A';

export interface FallingToken {
  color: Color;
  x: number;
  y: number;
  targetLane: number;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number; // 1 → 0
  color: string;
  r: number;
}

export class Renderer {
  readonly canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  cssW = 0;
  cssH = 0;
  private dpr = 1;
  private particles: Particle[] = [];
  /** lane index → remaining seconds of amber near-miss flash */
  private laneFlash: number[] = new Array(LANE_COUNT).fill(0);
  /** decaying "recent chain count" used to intensify the beat pulse after a clear */
  private recentChains = 0;
  private ro: ResizeObserver | null = null;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2D canvas unavailable');
    this.ctx = ctx;
    if (typeof ResizeObserver !== 'undefined') {
      this.ro = new ResizeObserver(() => this.resize());
      // Observe the wrapper, not the canvas: the canvas's own box is driven by
      // its width/height attributes, which resize() writes — observing it would
      // feed back. The wrapper is plain layout.
      this.ro.observe(canvas.parentElement ?? canvas);
    }
  }

  dispose(): void {
    this.ro?.disconnect();
  }

  get cellW(): number {
    return this.cssW / LANE_COUNT;
  }
  get cellH(): number {
    return this.cssH / ROW_COUNT;
  }

  resize(): void {
    // Measure the wrapper (plain layout), never the canvas — the canvas's box
    // follows its width/height attributes, which we set below.
    const box = this.canvas.parentElement ?? this.canvas;
    this.cssW = box.clientWidth;
    this.cssH = box.clientHeight;
    if (this.cssW === 0 || this.cssH === 0) return;
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.round(this.cssW * this.dpr);
    const h = Math.round(this.cssH * this.dpr);
    if (this.canvas.width !== w) this.canvas.width = w;
    if (this.canvas.height !== h) this.canvas.height = h;
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  }

  laneCenterX(lane: number): number {
    return lane * this.cellW + this.cellW / 2;
  }

  /** Screen y for the centre of grid row `row` (row 0 = bottom). */
  rowCenterY(row: number): number {
    return this.cssH - (row + 0.5) * this.cellH;
  }

  laneFromX(x: number): number {
    return Math.max(0, Math.min(LANE_COUNT - 1, Math.floor(x / this.cellW)));
  }

  // ── effects ───────────────────────────────────────────────────────────────
  burst(chains: Chain[]): void {
    this.recentChains = Math.max(this.recentChains, chains.length);
    for (const c of chains) {
      const glow = COLOR_GLOW[c.color];
      for (let r = c.startRow; r < c.startRow + c.length; r++) {
        const cx = this.laneCenterX(c.laneIndex);
        const cy = this.rowCenterY(r);
        const n = 10;
        for (let i = 0; i < n; i++) {
          const a = (i / n) * Math.PI * 2 + Math.random() * 0.5;
          const sp = 90 + Math.random() * 160;
          this.particles.push({
            x: cx,
            y: cy,
            vx: Math.cos(a) * sp,
            vy: Math.sin(a) * sp,
            life: 1,
            color: glow,
            r: 3 + Math.random() * 3,
          });
        }
      }
    }
    if (this.particles.length > 400) this.particles.splice(0, this.particles.length - 400);
  }

  flashLane(lane: number): void {
    this.laneFlash[lane] = 0.45;
  }

  // ── frame ─────────────────────────────────────────────────────────────────
  draw(opts: {
    grid: Grid;
    token: FallingToken | null;
    /** first empty row in the token's target lane, or -1 if the lane is full */
    targetLandingRow: number;
    dt: number;
    beatPosMs: number;
    bpm: number;
    queue: Color[];
    /** transient coaching line drawn near the bottom (first session only) */
    hint?: string | null;
  }): void {
    const { ctx } = this;
    const { grid, token, targetLandingRow, dt, beatPosMs, bpm, queue, hint } = opts;
    const W = this.cssW;
    const H = this.cssH;
    if (W === 0 || H === 0) return; // layout not ready yet
    const cw = this.cellW;
    const ch = this.cellH;

    this.recentChains = Math.max(0, this.recentChains - dt * 2);

    ctx.fillStyle = BG;
    ctx.fillRect(0, 0, W, H);

    // lane dividers
    ctx.strokeStyle = LANE_BORDER;
    ctx.lineWidth = 1;
    for (let l = 1; l < LANE_COUNT; l++) {
      ctx.beginPath();
      ctx.moveTo(l * cw, 0);
      ctx.lineTo(l * cw, H);
      ctx.stroke();
    }

    // beat-grid pulse — a glow band near the top
    const phase = beatPosMs > 0 ? inferBeatPhase(beatPosMs, bpm) : 1;
    const alpha = beatPulseOpacity(phase, chainMaxOpacity(Math.round(this.recentChains)));
    const bandY = ch * 0.5;
    const grad = ctx.createLinearGradient(0, bandY - ch, 0, bandY + ch);
    grad.addColorStop(0, `rgba(255,255,255,0)`);
    grad.addColorStop(0.5, `rgba(255,255,255,${(alpha * 0.5).toFixed(3)})`);
    grad.addColorStop(1, `rgba(255,255,255,0)`);
    ctx.fillStyle = grad;
    ctx.fillRect(0, bandY - ch, W, ch * 2);
    ctx.strokeStyle = `rgba(255,255,255,${alpha.toFixed(3)})`;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, bandY);
    ctx.lineTo(W, bandY);
    ctx.stroke();

    // placed tokens
    for (let l = 0; l < LANE_COUNT; l++) {
      for (let r = 0; r < ROW_COUNT; r++) {
        const c = grid[l][r];
        if (!c) continue;
        this.token(this.laneCenterX(l), this.rowCenterY(r), c, Math.min(cw, ch) * 0.38, 1);
      }
    }

    // near-miss lane flash
    for (let l = 0; l < LANE_COUNT; l++) {
      if (this.laneFlash[l] > 0) {
        const a = Math.min(1, this.laneFlash[l] / 0.45);
        ctx.strokeStyle = `rgba(255,176,0,${(a * 0.9).toFixed(3)})`;
        ctx.lineWidth = 3;
        ctx.strokeRect(l * cw + 1.5, 1.5, cw - 3, H - 3);
        this.laneFlash[l] = Math.max(0, this.laneFlash[l] - dt);
      }
    }

    // falling token + drop guide
    if (token) {
      const cx = this.laneCenterX(token.targetLane);
      const r = Math.min(cw, ch) * 0.4;
      const hex = COLOR_PALETTE[token.color];

      // faint tint on the whole target lane so it reads as "selected"
      ctx.fillStyle = hex + '12';
      ctx.fillRect(token.targetLane * cw, 0, cw, H);

      if (targetLandingRow >= 0) {
        const landY = this.rowCenterY(targetLandingRow);
        // dashed guide line: this token → where it will land
        ctx.save();
        ctx.strokeStyle = hex + '55';
        ctx.lineWidth = 2;
        ctx.setLineDash([3, 7]);
        ctx.beginPath();
        ctx.moveTo(cx, Math.min(token.y + r, landY));
        ctx.lineTo(cx, landY - r * 0.9);
        ctx.stroke();
        ctx.restore();
        // hollow ring at the landing cell — NOT a filled token, so it never
        // looks like a placed piece
        ctx.strokeStyle = hex;
        ctx.globalAlpha = 0.5;
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.arc(cx, landY, r * 0.92, 0, Math.PI * 2);
        ctx.stroke();
        ctx.globalAlpha = 1;
      } else {
        // lane full — you can't drop here
        ctx.strokeStyle = '#FF5C5C';
        ctx.lineWidth = 3;
        const a = token.targetLane * cw + cw * 0.32;
        const b = token.targetLane * cw + cw * 0.68;
        ctx.beginPath();
        ctx.moveTo(a, ch * 1.4); ctx.lineTo(b, ch * 2.0);
        ctx.moveTo(b, ch * 1.4); ctx.lineTo(a, ch * 2.0);
        ctx.stroke();
      }

      // the falling token (primary element)
      this.token(token.x, token.y, token.color, r, 1);
    }

    // particles
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vy += 220 * dt; // gravity
      p.life -= dt * 1.6;
      if (p.life <= 0) {
        this.particles.splice(i, 1);
        continue;
      }
      ctx.globalAlpha = Math.max(0, p.life);
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r * p.life, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;

    // queue preview — next 3 dots, top-right
    for (let i = 0; i < Math.min(3, Math.max(0, queue.length - 1)); i++) {
      const col = queue[i + 1];
      ctx.globalAlpha = i === 0 ? 1 : i === 1 ? 0.7 : 0.45;
      ctx.fillStyle = COLOR_PALETTE[col];
      ctx.beginPath();
      ctx.arc(W - 18 - i * 22, 18, 7, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;

    // first-session coaching line
    if (hint) {
      ctx.font = '600 13px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const tw = ctx.measureText(hint).width;
      const py = H - ch * 0.9;
      ctx.fillStyle = 'rgba(13,13,26,0.82)';
      this.roundRect(W / 2 - tw / 2 - 14, py - 15, tw + 28, 30, 15);
      ctx.fill();
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      ctx.fillText(hint, W / 2, py);
      ctx.textAlign = 'start';
      ctx.textBaseline = 'alphabetic';
    }
  }

  private roundRect(x: number, y: number, w: number, h: number, r: number): void {
    const { ctx } = this;
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  private token(x: number, y: number, color: Color, r: number, alpha: number): void {
    const { ctx } = this;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.shadowColor = COLOR_GLOW[color];
    ctx.shadowBlur = r * 0.6;
    ctx.fillStyle = COLOR_PALETTE[color];
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  hasActiveEffects(): boolean {
    return this.particles.length > 0 || this.laneFlash.some((f) => f > 0);
  }
}
